/**
 * A Backbone view to augment an input field with an error tooltip.  Example:
 *
 * var field = new MarkedField({
 *     el: myInputElement, // we'll wrap this element up with some magic
 *     dataType: 'currency', // or 'number' or 'percent' or null/undefined for text
 *     currencyCode: 'USD',
 *     precision: 3,
 *     // transforms field value into a value the server can accept
 *     evaluator: numericEvaluator,
 *     // validates the value that would be sent to the server
 *     validator: numericValidator // or [requiredValidator, numericValidator]
 *     // forces the input box to grow so all of the text remains visible
 *     // the default is false
 *     resizeWithText: true
 *	   lineHeight: 27,
 *	   // optionally specifies the line height of the input or textarea component.
 *	   // MarkedField will measure the line height if not supplied, which is the
 *	   // normal way it's initialized. However, when dealing with hundreds or
 *	   // thousands of MarkedFields as is done on the metric updates, supplying the
 *	   // line height improves the user experience by seconds
 *	   fieldPadding: 12,
 *	   // optionally specifies the fieldPadding of the input or textarea component.
 *	   // MarkedField will measure the fieldPadding if not supplied, which is the
 *	   // normal way it's initialized. However, when dealing with hundreds or
 *	   // thousands of MarkedFields as is done on the metric updates, supplying the
 *	   // field padding improves the user experience by seconds
 *	   markWidthCalculator: function,
 *	   // optionally specifies the code that should measure how wide the marker is
 *	   // By default, the code will measure the width of the marker for this
 *	   // MarkedField. Normally that's fine, but in cases like the metric updates
 *	   // measuring the width of the markers takes a lot of time. Since all
 *	   // markers which contain the same text will have the same width, the function
 *	   // passed here will be able to measure the width once per type and reuse
 *	   // that measurement
 *	   inputDisplay: 'inline-block',
 *	   // optionally specifies the css display value of the input element.
 *	   // By default this value will be measured, but the measurement can take at least 33ms
 *	   // which can be avoided if the creator knows what the applied css properties are.
 *	   // This is important for screens like update metrics that have a lot MarkedFields
 *	   movePercentPlacesBy: 1,
 *	   // How many places, to the right, should the decimal be moved when
 *	   // writing a percent value, and to the left, when reading it.
 *	   // Has no effect if dataType is not 'percent', and defaults to 2
 *	   tooltipOptions: {}
 *	   // options to pass to the tooltip component
 * }).render() // IMPORTANT: must call render
 *
 * When you're ready to validate the field, you can call
 *
 * var errorMsg = field.validate()
 *
 * Other options:
 *
 * // manually display an error message
 * field.error(i18n.get('some.error.message'))
 * // clear any errors and their tooltips
 * field.clearError()
 *
 * // get/set the values
 * field.val('new value')
 * var value = field.val()
 */

import $ from 'jquery'

import Backbone from 'backbone'
import _ from 'lodash'
import Tooltip from 'scoreboard/components/tooltip'
import currency from 'scoreboard/components/currency'
import config from 'scoreboard/components/config'
import moveDecimal from './move-decimal'
import textLength from './text-length'
import deviceType from 'scoreboard/components/device-type'
import locale from 'scoreboard/components/locale'
import icon from 'scoreboard/components/icon'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import momentHelper from 'scoreboard/components/moment-helper'
import KeyCodes from 'scoreboard/components/key-codes'
const { ESC } = KeyCodes
import constants from 'scoreboard/components/constants'
import isPercentDisplayed from 'scoreboard/components/is-percent-displayed'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, CURRENCY, DATE } = DataType
import formatValue from './format-value'

// can't use event.relatedTarget because it doesn't work in Safari or FF
// fortunately, mousedown events are fired before blur, so watch all mouse down
// events so we can check what was clicked on when a blur event is fired
var lastMouseDownEvent = { target: null }

$(function() {
	$('body').mousedown(function(e) {
		lastMouseDownEvent = e
	})
})

function markerContent(options) {
	if (options.markerContent) {
		return options.markerContent
	}

	var dataType = options.dataType || NUMBER
	var currencyCode = options.currencyCode || config.defaultCurrencyCode
	var unit = options.unit
	var mc = ''
	if (isPercentDisplayed(dataType)) { 
		mc = options.el?.dataset?.markerContent || '%'
	}
	if (dataType === NUMBER) {
		mc = unit || ''
	}
	if (dataType === CURRENCY) {
		if (currency.useCode(currencyCode)) {
			mc = currencyCode
		} else {
			mc = currency.forCode(currencyCode).symbol
		}
	}
	return mc
}

var MarkedField = Backbone.View.extend({

	className: 'marked-field',

	events: {
		'click .marker': '_markerClicked',
		'keyup input, textarea': '_onKeyup',
		'blur input, textarea': '_onBlur',
		'change select': '_onBlur',
		// we need to listen to the change event so that validation is invoked
		// when a mobile device changes the value via a native widget
		'change input, textarea, select': '_onKeyup'
	},

	initialize: function(options) {
		this.options = options || {}
		this.message = this.options?.message

		// These options can be set in advance for performance reasons. Placing the mark
		// and adjusting the input field so that text is not obscured by the mark
		// requires the mark to be measured. Each measurement will take about 33ms (chrome).
		// While this is normally not a problem, dozens or hundreds of fields with
		// marks will make the user experience pretty slow. Also note the options
		// object property inputDisplay which is also used.
		//
		// The metric updates screen suffers from this problem but has consistently
		// formatted input fields. This allows it to specify what the measurement
		// will be (lineHeight and fieldPadding) or reuse a measurement
		// (markWidthCalculator). This is a huge speed improvement on Chrome but
		// makes the difference between usability and unusability on IE.
		this.lineHeight = this.options.lineHeight
		this.fieldPadding = this.options.fieldPadding ?? 0
		this.markWidthCalculator = this.options.markWidthCalculator || this.markWidthCalculator

		// When a new dialog is opened, hide existing tooltips
		Backbone.on('dialog:open', this.removeTooltip.bind(this))

		_.bindAll(this, '_drawTooltip', 'removeTooltip', '_unmark', 'mark',
				'error')
	},

	/**
	* Sets the message and resets the cursor css value
	*/
	_setMessage: function(message) {
		// message passed in options always gets precedence over any messages from
		// the field's validators
		if (_.isNil(message) || !_.isNil(this.options.message)) return

		if (message.trim() === '') {
			this.$('.marker').css('cursor', '')
			delete this.message
		} else {
			this.$('.marker').css('cursor', 'pointer')
			this.message = message
		}
	},

	render: function() {
		if (!this.$el.parent().hasClass('marked-field')) {
			// set the view on the form control, e.g. the input
			this.$el.data('view', this)

			// In the following, .nextAll() and .addBack() will
			// only affect the parent if the input is a select
			var parent = this.$el.nextAll('.select') // If input is a select, include rich select
				.addBack() // Also include original native select (if its a select)
				.wrapAll('<span class="marked-field"></span>') // Use .wrapAll() instead of .wrap() to wrap both the native and rich select
				.parent()

			var markedField = parent
			markedField.toggleClass('marked-field-block',
				(this.options.inputDisplay || markedField.find('input, textarea').css('display')) === 'block')
			this._markedElement = this.$el
			this.setElement(parent.get(0))
			var mc = markerContent(this.options)
			this.$el.append('<span class="marker">' + mc + '</span>')

			if (this.message) {
				this.$('.marker').css('cursor', 'pointer')
			}
			this.padInputField()
		}
		// also make sure the view is set on the .marked-field element
		this.$el.data('view', this)

		var input = this.$('input')
		if (isPercentDisplayed(this.options.dataType)) {
			if (!_.isEmpty(input.val())) {
				const places = this.options.movePercentPlacesBy || 2
				input.val(moveDecimal(input.val(), places))
			}
		}
		// show commas in the output, which matches SB2 and our decision here:
		// https://basecamp.com/1979020/projects/5791995/messages/48290167#comment_363915886
		// #46368 only for numeric data types
		if (isNumericDataType(this.options.dataType) && !_.isEmpty(input.val())) {
			input.val(getEditableNumberText(input.val(), locale().locale,
				this.options.dataType, this.options.precision))
		}

		if (!this.skipMarkOnRender) {
			this.mark()
		}

		return this
	},

	// make the marked-field disabled, like in
	// home/5. Update Metrics/update-metrics-by-readonly.psd
	disable: function() {
		this.$el.addClass('is-disabled')
		this.$('select, input, textarea').prop('disabled', true)
	},

	// define these as functions in case an input gets removed right away
	// (e.g. in a unit test)
	_hasInput: function() {
		return this.$('input').length > 0
	},
	_hasTextarea: function() {
		return this.$('textarea').length > 0
	},
	_hasSelect: function() {
		return this.$('select').length > 0
	},

	_drawTooltip: function(options) {

		// If this.el isn't in the body, don't attempt to draw the tooltip since there's nothing to attach it to
		if (!$('body')[0].contains(this.el)) {
			return
		}

		// If a new message is given, replace the current message
		if (options?.message) {
			this.message = options.message
		}

		if (this.suppressed) {
			// ensure there's no tooltip so a new one is created in this._markerClicked()
			this.tooltip = null
			return
		}

		if (this.tooltip) {
			this.tooltip.content(this.message)
			return
		}

		// Create the tooltip
		// Set ignoreAttachToClick so that tooltip.js doesn't eat the marker click event, preventing
		// _markerClicked() from being called.
		this.tooltip = new Tooltip(_.defaults({}, options, this.options.tooltipOptions, {
			className: 'tooltip has-error',
			attachTo: this.$('.marker'),
			placement: 'above',
			arrow: 'center',
			content: this.message,
			closeOnChangedFocus: false,
			ignoreAttachToClick: true
		})).render()
	},

	/**
	* Marks this field with an informational icon and optional tooltip but not
	* in an error state.  Or, called with no argument clears errors
	*/
	mark: function(options) {
		// Reset the state of tooltipHidden since the field is being freshly marked
		this.tooltipHidden = false
		_.extend(this.options, options)

		// update marker if appropriate
		var mc = markerContent(this.options)
		this._unmark(mc.length)
		if (mc) {
			// this is hidden to start with so we avoid a repaint of this component
			// after the padding is added.
			this.$('.marker').css('visibility', 'hidden').text(mc)
			// Calculating the width will only work once the field has been added
			// to the DOM
			this.calculateMarkerAndFieldWidth()
			this.$('.marker').css('visibility', '')
			this.$el.addClass('is-marked')
		}

		this.removeTooltip()
		return this
	},

	updateMarkerContent: function() {
		let mc = markerContent(this.options)
		this.$el.find('span.marker').html(mc)
	},

	/**
	 * Changes the padding-right of the input field so that we allow space for
	 * the marker and keep the visual space from the original text box.
	 * This must be called after the marker has rendered in order to pick up
	 * an accurate width for the marker.
	 */
	padInputField: function() {
		// as we discovered in issue #5295, we need to make sure that the height
		// of the input element is a "real" height before we use that height to
		// set up the marker. At least one reason the height may not be "real" is
		// that the element is itself `display: none` or is a child of an element
		// which is `display: none`.
		if (!this.lineHeight) {
			// use _.result in case the input gets removed before we make it
			// here
			this.lineHeight = _.result(this.$('input, .select, textarea'), '0.offsetHeight')
		}
		var markerProperties = {
			'visibility': ''
		}
		if (this.lineHeight) {
			// Use 2 pixels less to account for the two pixel border of the marker
			markerProperties['line-height'] = this.lineHeight + 'px'
		}
		this.$('.marker').css(markerProperties)

		if (!this.fieldPadding && (this._hasInput() || this._hasTextarea())) {
			// figures out how many pixels (as an int) is the padding-right
			// of input field, if we're wrapping an input field
			var cssPaddingRight = /\d+/.exec(this.$('input, textarea').css('padding-inline-end'))
			if (cssPaddingRight && cssPaddingRight.length > 0) {
				this.fieldPadding = parseInt(this.$('input, textarea').css('padding-inline-end'))
			} else {
				this.fieldPadding = 0
			}

			this.inputWidth = parseInt(this.$('input, textarea').outerWidth())
		}

	},
	calculateMarkerAndFieldWidth: function() {
		let rightSpacing = this.markWidthCalculator(markerContent(this.options), this.$('.marker')) + this.fieldPadding
		this.$('input, textarea').css('padding-inline-end', rightSpacing)
		this.$('.type-icon').css('margin-inline-end', rightSpacing - this.fieldPadding)
		let selectSpacing = constants.STANDARD_MARGIN/2
		this.$('.select-item').css('margin-inline-end', rightSpacing + selectSpacing*3)
		this.$('.select .caret').css('inset-inline-end', rightSpacing + selectSpacing)
	},

	markWidthCalculator: function(markerText, marker) {
		return marker.outerWidth()
	},

	/**
	 * Clear the error mark and related tooltip
	 */
	clearError: function() {
		this.mark()
		this.removeTooltip()
		this.$el.removeClass('has-error')
	},

	/**
	* Marks this field as an error with mark and optional tooltip.
	*/
	error: function(message) {
		// Add the `has-error` class on the inner marked element, but not the wrapper. That needs to be applied on nextTick. See below
		if (this._markedElement) {
			this._markedElement.addClass('has-error')
		}

		// Check to see if the error will be covered by anything or out of
		// view, in case we need to suppress the error
		this._checkIsInView()

		process.nextTick(function() {
			// we need to do this in the next tick so we can accurately measure
			// how much space the text and text area take up.

			// we also need to unmark (clear the marking) in the same tick as we
			// add the error otherwise the marked field will jiggle when it gets
			// marked as an error.
			if (!this.$el.hasClass('has-error')) {
				this._unmark()
				this.$('.marker').css('visibility', 'hidden')
				if (_.isEmpty(this.$('.marker').text())) {
					this.$('.marker').html(icon('error-marker'))
				}
				this.calculateMarkerAndFieldWidth()
				this.$('.marker').css('visibility', '')
				this.$el.addClass('has-error')
			}
			if (!_.isEmpty(message)) {
				// If there's no tooltip yet or the message hasn't been updated yet,
				// draw/update the tooltip
				if (!this.tooltipHidden && (!this.tooltip || message != this.message)) {
					this._drawTooltip({ message: message })
				} else {
					// needed in case the field moved during validation, which
					// is likely if there was an error message at the top of
					// the screen which is gone now
					this.repositionTooltip()
				}
			}
			this._setMessage(message)
		}.bind(this))
		return this
	},

	// See if the .marked-field is in or out of view, if so, suppress it, if not,
	// unsuppress it. We're explicitely suppressing or unsuppressing fields each time
	// instead of just suppressing or doing nothing because we want to keep the state
	// up to date as the situation might change with scrolling/window resizing
	_checkIsInView: function() {
		this.suppressed = false

		// Is the field inside a .dialog?
		if (this.$el.closest('.dialog-body').length > 0 &&
			this.$el.closest('.dialog-body')[0].contains(this.el)) {
			let dialog = this.$el.closest('.dialog')

			// Is the .dialog-header covering the field?
			var dialogHeader = dialog.find('.dialog-header:visible')
			if (dialogHeader.length > 0 && this.el.getBoundingClientRect().top <= dialogHeader[0].getBoundingClientRect().bottom) {
				this.suppressed = true
				return
			}

			// Is the .dialog-footer covering the field?
			var dialogFooter = dialog.find('.dialog-footer:visible')
			if (dialogFooter.length > 0 && this.el.getBoundingClientRect().bottom >= dialogFooter[0].getBoundingClientRect().top) {
				this.suppressed = true
				return
			}

			// Return, don't bother checking non dialog constraints
			return
		}

		// Is the header covering the field?
		var displayHeader = $('.display-pane header:visible')
		if (displayHeader.length > 0 && this.el.getBoundingClientRect().top <= displayHeader[0].getBoundingClientRect().bottom) {
			this.suppressed = true
			return
		}

		// Is the footer covering the field?
		var displayFooter = $('.display-pane footer:visible')
		if (displayFooter.length > 0 && this.el.getBoundingClientRect().bottom >= displayFooter[0].getBoundingClientRect().top) {
			this.suppressed = true
		}
	},

	/*
	* Removes any marks
	*/
	_unmark: function(validated) {
		// If removing error marker from validation
		if (validated) {
			this.$el.removeClass('has-error')
		} else {
			// If manually removing a custom marking
			this.$el.removeClass('is-marked')
		}

		if (this._markedElement) {
			this._markedElement.removeClass('has-error')
		}

		// Removes the padding we may have added as part of the creating space
		// for the marker. This should work because the original padding should
		// be in the css, not as a style on the element itself.
		if (!this.$el.hasClass('is-marked')) {
			this.$('input, textarea').css('padding-inline-end', '')
			this.$('.select .caret').css('inset-inline-end', '')
		}
		this.$('.type-icon').css('margin-inline-end', '')

		this.removeTooltip()

		return this
	},

	// Called each time the marker is clicked.
	// If the tooltip is up, set this.tooltipHidden to show that the tooltip was
	// manually hidden (so it wont appear again on blur) and nix the tooltip.
	// Else, draw the tooltip. If we know that the tooltip was suppressed and
	// there is an error message, we should draw the tooltip.
	_markerClicked: function(evt) {
		if (this.tooltip && !this.suppressed) {
			this.tooltipHidden = true
			this.removeTooltip()
		} else if (this.message) {
			this.suppressed = false
			this.tooltipHidden = false
			this._drawTooltip()
		}
	},

	// returns the value of the input after having been run through the
	// evaluator, but does NOT do any special handling of percents
	_val: function() {
		var val = this.$('input, select, textarea').val()
		if (this.options.evaluator) {
			val = this.options.evaluator.call(this.options.evaluator, val)
		}
		return val
	},

	// similar to calling .val on the input element, but applies the evaluator
	// function to the returned value and handles percent values
	val: function(newValue) {
		if (arguments.length > 0) {
			this.$('input, select, textarea').val(newValue)
			return
		}

		var val = this._val()
		if (isPercentDisplayed(this.options.dataType) && !_.isEmpty(val)) {
			const places = -(this.options.movePercentPlacesBy || 2)
			if (!_.isNaN(_.toNumber(val))) {
				val = moveDecimal(val, places)
			}
		}

		if (isNumericDataType(this.options.dataType)) {
			return _.isNil(val) ? null : parseFloat(val)
		}

		if (this.options.dataType === DATE) {
			if (_.isArray(val)) {
				return _.map(val, momentHelper.isoDate)
			} else {
				return momentHelper.isoDate(val)
			}
		}

		return val
	},

	_lastMouseDownEvent: function() {
		return lastMouseDownEvent
	},

	_onBlur: function(e) {
		// If there's a custom onBlur event defined, go with it instead
		if (this.options.onBlur) {
			this.options.onBlur()
			return
		}

		var target = $(lastMouseDownEvent.target)
		// it's kind of lame to listen for specific elements, but there isn't
		// really any other way to do it.  event.relatedTarget isn't always
		// populated, and you can't depend on timeouts because on click events
		// happen after the user releases the mouse, which could be a varying
		// amount of time.  (A user is free to hold down their mouse button for
		// as long as they feel like it)
		var cancel = target.hasClass('cancel')
		var outsideDisplayPane = target.closest('.head-pane, .nav-pane').length > 0
		// If marked-field is a select, prevent valdating when a blur event is triggered
		// from clicking on the select outside of the nested textarea. We can tell the select
		// is still focused if the menu is open. Issue #18691
		let selectHasMenu = this.$('.select').hasClass('has-menu')
		if ((!lastMouseDownEvent || (!cancel && !outsideDisplayPane)) && !selectHasMenu) {
			this.validate()
		}
	},

	// validates the field, and returns an error message if an error was found
	// if no error was found, doesn't return anything.  does NOT popup an error
	// tooltip
	test: function(value) {
		if (_.isUndefined(value)) {
			value = this._val()
		}

		// accept term 'validator' or 'validators'
		var validators = this.options.validator || this.options.validators
		if (!validators) {
			return
		}

		// if just a single validator is supplied, wrap it in an array
		if (_.isFunction(validators)) {
			validators = [validators]
		}

		for (var i=0; i<validators.length; i++) {
			var validator = validators[i]
			var errorMsg = validator.call(validator, value)
			if (errorMsg) {
				return errorMsg
			}
		}
	},

	// validates the field, and returns an error message if an error was found
	// if no error was found, doesn't return anything.  ALSO popup an error
	// tooltip
	validate: function(value) {
		var errorMsg = this.test(value)
		this.trigger('validate', {value: value, err: errorMsg})
		if (errorMsg) {
			this.error(errorMsg)
			this.validateOnInput = true
			return errorMsg
		} else {
			this._unmark(true)
		}
	},

	// repositions the error tooltip, if there is one
	repositionTooltip: function() {
		process.nextTick(function() {
			if (!this.tooltip) return

			if ($(this.tooltip.options.attachTo).is(':visible')) {
				// the positions of the components won't be final until the next
				// tick, so we have to wait till then to position the tooltip
				this.tooltip.reposition()
			} else {
				this.removeTooltip()
			}
		}.bind(this))
	},

	/**
	 * Intelligently redraws the components in the marked field so its tooltip
	 * is in the correct location. If the field is no longer visible, hides the
	 * tooltip.
	 */
	redraw() {
		if (!this.tooltip) return

		if (this.$el.is(':visible')) {
			this.validate()
			// my first try was these two lines, but the tooltip ended up
			// upside down, so I just called validate() to get this working.
			// that approach may not be the best, but time will tell
			// this.tooltip.show()
			// this.tooltip.reposition()
		} else {
			this.tooltip.hide()
		}
	},

	_onKeyup: function(e) {
		if (e.keyCode == ESC) {
			return
		}

		if (this.options.resizeWithText) {
			this.resize()
		}

		if (!this.validateOnInput) {
			return
		}

		var val = this._val()
		var errorMsg = this.validate(val)
		this._changeErrorMessage(errorMsg)
	},

	_changeErrorMessage: function(errorMsg) {
		// if there is no error, make sure the mark isn't red
		if (!errorMsg) {
			this.mark()
		}
	},

	/**
	 * Resizes the field so that there is enough space to show all the text.
	 */
	resize: function(spaceRemaining) {
		var spaceRemaining = this.getSpaceForContent() - textLength(this.$('input'))
		var maxCharWidth = this.getMaxCharWidth()
		var wrapper = this.$el
		var inputWidth = this.getWidth()
		if (!this.startingWidth) {
			this.startingWidth = wrapper[0].clientWidth
		}

		if (spaceRemaining < maxCharWidth) {
			var width = inputWidth + maxCharWidth - spaceRemaining
			wrapper.css('min-width', width)
		} else if (spaceRemaining > (2 * maxCharWidth) && inputWidth > this.startingWidth) {
			var width = Math.max(inputWidth - spaceRemaining + maxCharWidth, this.startingWidth)
			wrapper.css('min-width', width)
		}

	},

	/**
	 * Returns the number of pixels currently available for text content.
	 */
	getSpaceForContent: function() {
		var input = this.$('input')
		if (input) {
			var space = input[0].clientWidth
			space -= parseInt(input.css('padding-inline-start')) +
				parseInt(input.css('padding-inline-end'))
			return space
		}
	},

	/*
	 * Returns the width of the input box
	 */
	getWidth: function() {
		var input = this.$('input')
		if (input) {
			var space = input[0].clientWidth
			return space
		}
	},

	/*
	 * Returns the width of the largest single character we could see next.
	 */
	getMaxCharWidth: function() {
		if (!this.maxCharWidth) {
			this.maxCharWidth = textLength(this.$('input'), 'M')
		}
		return this.maxCharWidth
	},

	/*
	 * Returns the width of the text currently in the input box.
	 */
	getContentWidth: function() {
		return textLength(this.$('input'))
	},

	// toggles whether the marked field should appear visible to the user
	toggle: function(display) {
		if (!_.isUndefined(display)) {
			display = !display
		}
		this.$el.toggleClass('is-not-displayed', display)
	},

	removeTooltip: function() {
		if (this.tooltip) {
			this.tooltip.remove()
			this.tooltip = null
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeTooltip()
	}

})

/**
 * Returns the correct format for editing text that represents a number.
 * (#56105)
 * @param {String} numberStr the number to format
 * @param {String} locale the locale to use, e.g. en-US
 * @param {DataType} dataType the data type of the value
 * @param {Number} [precision] the precision of the value
 */
function getEditableNumberText(numberStr, locale, dataType, precision) {
	const number = new Number(numberStr)

	// #56105 Update South Africa English number format sp it uses the same
	// grouping and decimal separators as the CLDR standard
	if (locale == 'en-ZA' ||
		// #54521 Number.toLocaleString() doesn't work as expected for the Arabic
		// locale, so we need to use formatValue to prevent validation issues
		locale == 'ar') {
		let formattingOptions = {
			value: numberStr,
			type: 'number',
			shorten: false
		}
		// if this is currency dataType, the number has a decimal portion
		// and the default currency precision is set, provide some currency
		// formatting options
		let useCurrencyPrecision = dataType === 'currency' && number % 1 > 0
			&& !_.isNil(config.defaultCurrencyPrecision)
		if (useCurrencyPrecision) {
			formattingOptions.dataType = 'currency'
			formattingOptions.omitCurrencySymbol = true
		}
		return formatValue(formattingOptions)
	}

	let options = { }
	if (dataType === 'currency' && parseInt(number) != number && config.defaultCurrencyPrecision) {
		// If the number is a non-integer currency, pad zeros to get to the default
		// currency precision
		options.minimumFractionDigits = precision ?? config.defaultCurrencyPrecision
		// 20 is the maximum allowed value; this ensures we won't be chopping
		// off digits that go past the currency precision
		options.maximumFractionDigits = 20
	} else {
		// For everything else, using the maximum value for maximumSignificantDigits
		// seems to work best
		options.maximumSignificantDigits = 21
	}

	return number.toLocaleString(locale, options)
}

_.extend(MarkedField, {
	getEditableNumberText
})
export default MarkedField
export { getEditableNumberText, markerContent }

MarkedField.__filename__ = 'components/marked-field.js'
